import React, { useState, useEffect } from 'react';
import { MessageSquare, Bug, Lightbulb, Star, AlertTriangle, CheckCircle, Clock, Filter } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';

interface FeedbackItem {
  id: string;
  user_id: string;
  type: 'feedback' | 'bug' | 'idea';
  feedback: string;
  rating?: number;
  error_details?: string;
  steps_to_reproduce?: string;
  created_at: string;
  user?: {
    display_name: string;
    email: string;
  };
}

export function FeedbackManagement() {
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'feedback' | 'bug' | 'idea'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'rating'>('date');

  useEffect(() => {
    fetchFeedback();
  }, [filter, sortBy]);

  const fetchFeedback = async () => {
    try {
      let query = supabase
        .from('feedback')
        .select(`
          *,
          user:profiles!feedback_user_id_fkey (
            display_name,
            email
          )
        `);

      if (filter !== 'all') {
        query = query.eq('type', filter);
      }

      const { data, error } = await query.order(
        sortBy === 'date' ? 'created_at' : 'rating',
        { ascending: false }
      );

      if (error) throw error;
      setFeedback(data || []);
    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'feedback':
        return <MessageSquare className="w-5 h-5 text-blue-500" />;
      case 'bug':
        return <Bug className="w-5 h-5 text-red-500" />;
      case 'idea':
        return <Lightbulb className="w-5 h-5 text-yellow-500" />;
      default:
        return null;
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const renderFeedbackItem = (item: FeedbackItem) => {
    return (
      <div key={item.id} className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            {getTypeIcon(item.type)}
            <div>
              <h3 className="font-medium capitalize">{item.type}</h3>
              <p className="text-sm text-gray-600">
                From: {item.user?.display_name || item.user?.email || 'Anonymous'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {item.rating && renderStars(item.rating)}
            <span className="text-sm text-gray-600">
              {format(new Date(item.created_at), 'MMM d, yyyy h:mm a')}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          {item.type === 'bug' ? (
            <>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">Error Details</h4>
                <p className="text-gray-600 bg-red-50 p-3 rounded-lg">
                  {item.error_details}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">Steps to Reproduce</h4>
                <p className="text-gray-600 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                  {item.steps_to_reproduce}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">Additional Details</h4>
                <p className="text-gray-600">{item.feedback}</p>
              </div>
            </>
          ) : (
            <p className="text-gray-600 whitespace-pre-wrap">{item.feedback}</p>
          )}
        </div>

        {item.type === 'bug' && (
          <div className="mt-4 flex items-center gap-2">
            <button className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm hover:bg-yellow-200 flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>In Progress</span>
            </button>
            <button className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm hover:bg-green-200 flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              <span>Resolved</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Feedback Management</h2>
        <div className="flex items-center gap-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as typeof filter)}
            className="px-3 py-2 border rounded-md text-sm"
          >
            <option value="all">All Types</option>
            <option value="feedback">Feedback</option>
            <option value="bug">Bugs</option>
            <option value="idea">Ideas</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            className="px-3 py-2 border rounded-md text-sm"
          >
            <option value="date">Sort by Date</option>
            <option value="rating">Sort by Rating</option>
          </select>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="w-5 h-5 text-blue-500" />
            <h3 className="font-medium">Total Feedback</h3>
          </div>
          <p className="text-2xl font-bold text-blue-700">
            {feedback.filter(f => f.type === 'feedback').length}
          </p>
        </div>

        <div className="bg-red-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Bug className="w-5 h-5 text-red-500" />
            <h3 className="font-medium">Reported Bugs</h3>
          </div>
          <p className="text-2xl font-bold text-red-700">
            {feedback.filter(f => f.type === 'bug').length}
          </p>
        </div>

        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-5 h-5 text-yellow-500" />
            <h3 className="font-medium">New Ideas</h3>
          </div>
          <p className="text-2xl font-bold text-yellow-700">
            {feedback.filter(f => f.type === 'idea').length}
          </p>
        </div>

        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-5 h-5 text-green-500" />
            <h3 className="font-medium">Avg Rating</h3>
          </div>
          <p className="text-2xl font-bold text-green-700">
            {(feedback
              .filter(f => f.rating)
              .reduce((acc, f) => acc + (f.rating || 0), 0) / 
              feedback.filter(f => f.rating).length
            ).toFixed(1)}
          </p>
        </div>
      </div>

      {/* Feedback List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8">
            <Clock className="w-6 h-6 animate-spin text-primary-600 mx-auto mb-2" />
            <p className="text-gray-600">Loading feedback...</p>
          </div>
        ) : feedback.length === 0 ? (
          <div className="text-center py-8 text-gray-600">
            No feedback found
          </div>
        ) : (
          feedback.map(renderFeedbackItem)
        )}
      </div>
    </div>
  );
}